import { useState, useCallback, type ReactNode } from "react";
import clsx from "clsx";

export interface CopyButtonProps {
  /** Text to write to the clipboard. */
  text:       string;
  /** Button label in idle state. Defaults to "Копировать". */
  children?:  ReactNode;
  className?: string;
}

const COPIED_MS = 1500;

export function CopyButton({ text, children, className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      // clipboard may be unavailable (non-secure context); fail silently
    }
  }, [text]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? "Скопировано" : "Скопировать в буфер"}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition-colors",
        copied
          ? "bg-[var(--color-success-muted)] text-success"
          : [
              "border border-border bg-surface text-muted",
              "hover:border-muted hover:text-fg",
            ],
        className,
      )}
    >
      {copied ? (
        <>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
            <path
              d="M2 6l3 3 5-5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Скопировано
        </>
      ) : (
        <>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
            <rect x="4" y="4" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.25" />
            <path
              d="M1 8V2a1 1 0 0 1 1-1h6"
              stroke="currentColor"
              strokeWidth="1.25"
              strokeLinecap="round"
            />
          </svg>
          {children ?? "Копировать"}
        </>
      )}
    </button>
  );
}
